import DynamicForm from "@/common/DynamicForm";
import { IntakeSectionHeader, StyledGridRow } from "@/common/styles";
import { Form } from "antd";
import { forwardRef, useEffect, useRef, useState } from "react";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";
import {
  convertApiFields,
  isNonEmptyVariable,
  hasCheckSecurity,
  getSQLTypeToHTMLType,
  debounce,
} from "@/util/common_function";
import { useDispatch, useSelector } from "react-redux";
import { setBookings } from "@/slices/bookingsSlice";
import dayjs from "dayjs";

// ArrestReportsExtra's own PK/FK columns, lower-first — mirrors how the
// Warrants module addresses WarrantsExtra's warrantExtraID/warrantID (see
// AdditionalWarrantInformation.jsx). NOTE: despite the table being named
// "ArrestReportsExtra" (plural), the API actually serializes its PK as
// "arrestReportExtraID" — singular "Report" — confirmed from a live
// booking response's nested arrestReports[0].arrestReportsExtra[].
// Using the plural spelling here meant this key never matched, so the
// snapshot's ID always read as falsy and every save wrongly went CREATE.
const ARREST_REPORTS_EXTRA_ID_KEY = "arrestReportExtraID";
const ARREST_REPORT_ID_COLUMN = "arrestReportID";

const AdditionalBookingInformation = (startingPoint = "") => {
  // NOTE: this component receives its whole props object positionally as
  // "startingPoint" (pre-existing — not something this change introduces),
  // so a prop passed as `onFieldsLoaded={...}` lands on startingPoint.onFieldsLoaded
  // rather than needing its own destructured parameter.
  const onFieldsLoaded = startingPoint?.onFieldsLoaded;
  const dispatch = useDispatch();
  const { bookings, bookingsFieldsEnabled } = useSelector(
    (state) => state?.bookings
  );
  const [form] = Form.useForm();
  const [apiFields, setApiFields] = useState([]);
  const [additionalBookingInfo, setAdditionalBookingInfo] = useState(null);
  // True once fieldHtmlTypeMapRef is populated — gates the effect below so
  // a date/datetime column's value is never handed to the form as a plain
  // string. This section's own field filter deliberately also includes any
  // column explicitly routed to "ArrestInformation" (e.g. intakeWarCheckDate,
  // towingCompany — see the filter comment below), not just its default
  // fields, so hardcoding just dnaTestDateCompleted/mantoux as "the" date
  // fields was never actually complete — intakeWarCheckDate's real SQL type
  // is "datetime", so it flowed through unconverted, and antd's DatePicker
  // crashed ("date.isValid is not a function") calling .isValid() on that
  // raw string once its Form.Item mounted with it.
  const [fieldsReady, setFieldsReady] = useState(false);
  const fullSearch = useSelector(state => state.fullSearch);
  const globalClientParam = useSelector(
    (state) => state.config.globalClientParam
  );

  const bookingsRef = useRef(bookings);
  useEffect(() => {
    bookingsRef.current = bookings;
  }, [bookings]);

  // columnName -> htmlType ("date" / "datetime" / ...) for every field this
  // section renders — same typeMap-driven approach
  // AdditionalPersonalInformation.jsx uses for BioExtra, so date/datetime
  // columns get dayjs<->string converted consistently on both sides of the
  // form regardless of which specific columns turn out to be dates.
  const fieldHtmlTypeMapRef = useRef({});

  // --- Fetch API Field Definitions ---
  useEffect(() => {
    getFieldsFromExtraAPI();
  }, []);

  // The saved ArrestReportsExtra row itself (with its real PK) is fetched
  // once, centrally, by useArrestReportsExtraSync in
  // BookingsDetails/index.jsx — not here. A second copy of that fetch used
  // to live in this component too; since each had its own private "in
  // flight" guard, unaware of the other, both would race a GET before
  // either's response landed in redux, so the two never actually
  // deduplicated against each other.

  useEffect(() => {
    // Wait for fieldHtmlTypeMapRef to be populated before touching the form
    // — see the fieldsReady comment above for why.
    if (!fieldsReady) return;

    if (isNonEmptyVariable(bookings)) {
      const abi = bookings?.arrestReports?.[0]?.arrestReportsExtra?.[0];
      // The DatePicker needs an actual dayjs instance to render a
      // date/datetime field's value — redux holds it as a plain string
      // (from the GET fetch, or already-formatted from a prior change), so
      // convert just the known date/datetime columns here rather than
      // guessing at any string that happens to look like a date.
      const withDates = abi
        ? Object.keys(abi).reduce((acc, key) => {
            const val = abi[key];
            const htmlType = fieldHtmlTypeMapRef.current[key];
            const isDateField = htmlType === "date" || htmlType === "datetime";
            acc[key] =
              isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
            return acc;
          }, {})
        : abi;
      setAdditionalBookingInfo(withDates);
    } else {
      setAdditionalBookingInfo(null);
    }
  }, [bookings, fieldsReady]);

  // Set the form’s initial values from formData (after transforming for display).
  useEffect(() => {
    if (isNonEmptyVariable(additionalBookingInfo)) {
      form.setFieldsValue(additionalBookingInfo);
    } else {
      form.resetFields();
    }
  }, [additionalBookingInfo]);

  const getFieldsFromExtraAPI = () => {
    api
      .get(API_ENDPOINTS.getBookingExtra)
      .then((response) => {
        let data = response?.data || [];

        // Step 1: Get paramContext (columnName) values that have been explicitly
        // routed to a DIFFERENT section (e.g. "ReleaseInformation") via
        // sp_pci_cfg_AddClientParam 'ArrestReportsExtra', '<Column>_U', '<DivName>'.
        // Scoped to paramName === "ArrestReportsExtra" so unrelated client params
        // (other tables, or "OverrideSelect" dropdown overrides) never collide here.
        const paramContexts = globalClientParam
          ?.filter(
            (p) =>
              p.paramName === "ArrestReportsExtra" &&
              p.paramValue !== "ArrestInformation"
          )
          ?.map((p) => p.paramContext) || [];

        // Step 2: Filter data
        const filteredData = data.filter((item) => {
          const columnName = item?.columnName;

          // Condition 1: must end with _U
          const endsWithU = columnName?.endsWith("_U");

          // Condition 2: must NOT be explicitly assigned to another section.
          // Fields with no assignment, or explicitly assigned to "ArrestInformation",
          // belong in this (default) section.
          const assignedElsewhere = paramContexts?.includes(columnName);

          return endsWithU && !assignedElsewhere;
        });

        // Step 3: Convert
        let converted = convertApiFields(filteredData, "ArrestReportsExtra");

        // Step 4: Apply date type
        converted = converted?.map((c) => {
          if (
            c?.columnName === "dnaTestDateCompleted" ||
            c?.columnName === "mantoux"
          ) {
            c.type = "date";
          }
          return c;
        });

        console.log("filtered + converted", converted);

        const typeMap = {};
        converted.forEach((c) => {
          typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type);
        });
        fieldHtmlTypeMapRef.current = typeMap;

        setApiFields(converted);
        setFieldsReady(true);
        onFieldsLoaded?.(converted.length > 0);
      })
      .catch((error) => {
        console.error(
          "Request failed:",
          error.response ? error.response.data : error.message
        );
        onFieldsLoaded?.(false);
      });
  };

  // Merge current visible form values with hidden fields from formData, then
  // transform keys before saving. Reads bookingsRef (not the `bookings`
  // component-scope variable) since this is called from a debounced
  // callback created once (see dataModifyRef below) — the ref always holds
  // the latest redux value, avoiding a stale closure.
  const updateForm = (currentValues) => {
    const arrestReport = bookingsRef.current?.arrestReports?.[0] || {};
    const existingExtra = arrestReport?.arrestReportsExtra?.[0] || {};

    // A date/datetime field's value is a live dayjs instance here — redux
    // must never hold on to one of those (it doesn't round-trip through
    // Redux/Immer's state the way a plain string does, which is what was
    // making a picked date disappear again on the next render). Format it
    // to a plain string before it leaves the form, same as
    // AdditionalPersonalInformation.jsx's updateForm does for BioExtra (and
    // clear it to null rather than leaving it out, so an emptied field
    // actually clears in redux instead of sticking around).
    const formattedValues = Object.keys(currentValues).reduce((acc, key) => {
      const val = currentValues[key];
      const htmlType = fieldHtmlTypeMapRef.current[key];

      if (val) {
        if (htmlType === "date") {
          acc[key] = dayjs(val).format("MM/DD/YYYY");
        } else if (htmlType === "datetime") {
          acc[key] = dayjs(val).format("MM/DD/YYYY HH:mm");
        } else {
          acc[key] = val;
        }
      } else {
        acc[key] = null;
      }
      return acc;
    }, {});

    dispatch(
      setBookings({
        arrestReports: [
          {
            ...arrestReport,
            arrestReportsExtra: [
              {
                ...existingExtra,
                ...formattedValues,
                [ARREST_REPORTS_EXTRA_ID_KEY]:
                  existingExtra?.[ARREST_REPORTS_EXTRA_ID_KEY] ?? 0,
                [ARREST_REPORT_ID_COLUMN]:
                  existingExtra?.[ARREST_REPORT_ID_COLUMN] ??
                  arrestReport?.arrestReportId ??
                  0,
              },
            ],
          },
        ],
      })
    );
  };

  // Debounced so rapid keystrokes/onFieldsChange events don't dispatch on
  // every single one — same pattern as ArrestBook.jsx's dataModify /
  // AdditionalPersonalInformation.jsx's dataModifyRef. Built once via
  // useRef rather than redeclared each render.
  const dataModifyRef = useRef(null);
  if (!dataModifyRef.current) {
    dataModifyRef.current = debounce(() => {
      updateForm(form.getFieldsValue());
    }, 800);
  }

  return (
    <>
      <Form
        form={form}
        onFieldsChange={() => {
          dataModifyRef.current();
        }}
        disabled={!bookingsFieldsEnabled}
      >
        <StyledGridRow gutter={[16]}>
          {
            apiFields?.map((sf, index) => {
              let view = hasCheckSecurity("Functionality", "field", "VIEW", "", "", "ArrestReportsExtra", sf.columnName, startingPoint)
              let edit = hasCheckSecurity("Functionality", "field", "MODIFY", "", "", "ArrestReportsExtra", sf.columnName, startingPoint)
              return (
                <DynamicForm
                  key={index}
                  formAttributes={sf}
                  form={form}
                  bookingsFieldsEnabled={bookingsFieldsEnabled}
                  fullSearch={fullSearch}
                  view={view}
                  edit={edit}
                />
              );
            })
          }
        </StyledGridRow>
      </Form>
    </>
  );
};

export default AdditionalBookingInformation;
